import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useUserContext } from "../context/UserContext";
import PageNavBar from "../components/PageNavBar";
import TenantBanner from "../components/TenantBanner";
import { getUserEmail } from "../utils/sessionCheck";
import {
  getPendingTimeBookings,
  approveTimeBooking,
  rejectTimeBooking,
  getSupervisedUsers,
} from "../context/Api";

const TimeBookingApproval = () => {
  const { userRole, tenantId, selectedTenantId } = useUserContext();
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const userEmail = getUserEmail();
  const effectiveTenantId =
    userRole === "SuperAdmin"
      ? selectedTenantId || sessionStorage.getItem("selectedTenantId")
      : tenantId || sessionStorage.getItem("selectedTenantId");

  // Check if user is a supervisor
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [checkingSupervisor, setCheckingSupervisor] = useState(true);

  useEffect(() => {
    const checkSupervisorStatus = async () => {
      if (!userEmail || !effectiveTenantId) {
        setCheckingSupervisor(false);
        return;
      }

      // Admins and SuperAdmins can access this page
      if (userRole === "admin" || userRole === "SuperAdmin") {
        setIsSupervisor(true);
        setCheckingSupervisor(false);
        return;
      }

      try {
        const supervisedUsers = await getSupervisedUsers(userEmail);
        setIsSupervisor(supervisedUsers.length > 0);
      } catch (err) {
        console.error("Error checking supervisor status:", err);
        setIsSupervisor(false);
      } finally {
        setCheckingSupervisor(false);
      }
    };

    checkSupervisorStatus();
  }, [userEmail, effectiveTenantId, userRole]);

  useEffect(() => {
    if (isSupervisor && !checkingSupervisor) {
      fetchPendingBookings();
    }
  }, [userEmail, effectiveTenantId, isSupervisor, checkingSupervisor]);

  const fetchPendingBookings = async () => {
    if (!userEmail || !effectiveTenantId) return;

    setLoading(true);
    setError("");
    try {
      const bookings = await getPendingTimeBookings(userEmail, effectiveTenantId, userRole);
      setPendingBookings(bookings);
    } catch (err) {
      console.error("Error fetching pending bookings:", err);
      setError("Failed to fetch pending bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (booking) => {
    setSelectedBooking(booking);
    setApprovalDialogOpen(true);
  };

  const handleReject = async (booking) => {
    setSelectedBooking(booking);
    setRejectionReason("");
    setRejectionDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedBooking) return;

    try {
      await approveTimeBooking(selectedBooking.id);
      setSuccess(`Time booking approved successfully`);
      setApprovalDialogOpen(false);
      setSelectedBooking(null);
      await fetchPendingBookings();
    } catch (err) {
      console.error("Error approving booking:", err);
      setError("Failed to approve booking");
    }
  };

  const confirmReject = async () => {
    if (!selectedBooking) return;

    try {
      await rejectTimeBooking(selectedBooking.id, rejectionReason);
      setSuccess(`Time booking rejected`);
      setRejectionDialogOpen(false);
      setSelectedBooking(null);
      setRejectionReason("");
      await fetchPendingBookings();
    } catch (err) {
      console.error("Error rejecting booking:", err);
      setError("Failed to reject booking");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const [day, month, year] = dateStr.split("-");
    const dateObj = new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
    return dateObj.toLocaleDateString();
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "N/A";
    return timeStr;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Show access denied if not a supervisor
  if (!checkingSupervisor && !isSupervisor) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          position: "relative",
        }}
      >
        <PageNavBar title="Time Booking Approval" backTo="/dashboard" />
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Alert severity="error">
            <Typography variant="h6">
              Access Denied
            </Typography>
            <Typography variant="body2">
              Only supervisors can access this page. You need to have supervised users assigned to you.
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  if (checkingSupervisor) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          position: "relative",
        }}
      >
        <PageNavBar title="Time Booking Approval" backTo="/dashboard" />
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "white" }}>Checking access...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
      }}
    >
      <PageNavBar title="Time Booking Approval" backTo="/dashboard" />
      <TenantBanner />

      <Box sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, color: "white" }}>
          Pending Time Booking Approvals
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Typography sx={{ color: "white" }}>Loading pending bookings...</Typography>
        ) : pendingBookings.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No pending time bookings to approve
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All time bookings from your supervised users have been processed.
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ overflowX: "auto", mt: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell>Date</TableCell>
                    <TableCell>User Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Task Description</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>{formatDate(booking.date)}</TableCell>
                      <TableCell>{booking.userName}</TableCell>
                      <TableCell>{booking.userEmail}</TableCell>
                      <TableCell>
                        {booking.taskDescription || booking.taskList || "N/A"}
                      </TableCell>
                      <TableCell>{formatTime(booking.startTime)}</TableCell>
                      <TableCell>{formatTime(booking.endTime)}</TableCell>
                      <TableCell>
                        {formatDuration(booking.totalTimeBooked)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.approvalStatus || "pending"}
                          size="small"
                          color={
                            booking.approvalStatus === "approved"
                              ? "success"
                              : booking.approvalStatus === "rejected"
                              ? "error"
                              : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {booking.releaseFlag ? (
                          <Chip label="Legacy Entry" size="small" color="info" />
                        ) : (
                          <Chip label="Regular" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(booking)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleReject(booking)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Approval Confirmation Dialog */}
        <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)}>
          <DialogTitle>Approve Time Booking</DialogTitle>
          <DialogContent>
            {selectedBooking && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography>
                  <strong>User:</strong> {selectedBooking.userName} ({selectedBooking.userEmail})
                </Typography>
                <Typography>
                  <strong>Date:</strong> {formatDate(selectedBooking.date)}
                </Typography>
                <Typography>
                  <strong>Duration:</strong> {formatDuration(selectedBooking.totalTimeBooked)}
                </Typography>
                <Typography>
                  <strong>Task:</strong> {selectedBooking.taskDescription || selectedBooking.taskList || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Are you sure you want to approve this time booking?
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="success" onClick={confirmApprove}>
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject Time Booking</DialogTitle>
          <DialogContent>
            {selectedBooking && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography>
                  <strong>User:</strong> {selectedBooking.userName} ({selectedBooking.userEmail})
                </Typography>
                <Typography>
                  <strong>Date:</strong> {formatDate(selectedBooking.date)}
                </Typography>
                <Typography>
                  <strong>Duration:</strong> {formatDuration(selectedBooking.totalTimeBooked)}
                </Typography>
                <TextField
                  label="Rejection Reason (Optional)"
                  multiline
                  rows={4}
                  fullWidth
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmReject}>
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default TimeBookingApproval;

